import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/useAuth';
import { useToast } from '../components/ui/useToast';
import { ErreurApi } from '../lib/api';
import {
  useChangerEmail,
  useChangerMotDePasse,
  useDemanderSuppression,
  useExporterDonnees,
  useModifierPreferences,
  useModifierProfil,
} from '../lib/useParametres';
import { useReduireAnimations } from '../lib/useReduireAnimations';

function Bascule({
  libelle,
  checked,
  onChange,
  disabled,
}: {
  /** Nom lu par les lecteurs d'écran : le texte affiché à côté. */
  libelle: string;
  checked: boolean;
  onChange: (valeur: boolean) => void;
  disabled?: boolean;
}): JSX.Element {
  // La case est masquée : son focus clavier se voit sur la bascule dessinée.
  return (
    <label className="relative inline-flex shrink-0 cursor-pointer items-center">
      <input
        type="checkbox"
        role="switch"
        aria-label={libelle}
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="peer sr-only"
      />
      <span
        className={`flex h-6 w-10 items-center rounded-full px-1 transition peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-codialis-blue ${
          checked ? 'bg-codialis-teal' : 'bg-gray-300'
        } ${disabled ? 'opacity-50' : ''}`}
      >
        <span
          className="h-4 w-4 rounded-full bg-white transition"
          style={{ transform: checked ? 'translateX(18px)' : 'translateX(0)' }}
        />
      </span>
    </label>
  );
}

function messageErreur(erreur: unknown, repli: string): string {
  return erreur instanceof ErreurApi ? erreur.message : repli;
}

export function SettingsPage(): JSX.Element {
  const navigate = useNavigate();
  const toast = useToast();
  const { utilisateur, installerSession, peutAcceder } = useAuth();

  const modifierProfil = useModifierProfil();
  const modifierPreferences = useModifierPreferences();
  const changerMotDePasse = useChangerMotDePasse();
  const changerEmail = useChangerEmail();
  const exporterDonnees = useExporterDonnees();
  const demanderSuppression = useDemanderSuppression();
  const [reduireAnimations, setReduireAnimations] = useReduireAnimations();

  const [prenom, setPrenom] = useState(utilisateur?.prenom ?? '');
  const [nom, setNom] = useState(utilisateur?.nom ?? '');
  const [telephone, setTelephone] = useState(utilisateur?.telephone ?? '');

  const [motDePasseActuel, setMotDePasseActuel] = useState('');
  const [nouveauMotDePasse, setNouveauMotDePasse] = useState('');

  const [modifierEmailOuvert, setModifierEmailOuvert] = useState(false);
  const [nouvelEmail, setNouvelEmail] = useState('');
  const [motDePasseEmail, setMotDePasseEmail] = useState('');

  if (!utilisateur) return <></>;

  const enregistrerProfil = (): void => {
    modifierProfil.mutate(
      { prenom: prenom.trim(), nom: nom.trim(), telephone: telephone.trim() },
      {
        onSuccess: (reponse) => {
          installerSession(reponse.utilisateur);
          toast('Préférences enregistrées');
        },
        onError: (erreur) => toast(messageErreur(erreur, "Impossible d'enregistrer le profil")),
      },
    );
  };

  const basculerPreference = (
    cle: 'alertesEmail' | 'alertesSms' | 'recapHebdo' | 'mfaActif',
    valeur: boolean,
  ): void => {
    modifierPreferences.mutate(
      { [cle]: valeur },
      {
        onSuccess: (reponse) => {
          installerSession(reponse.utilisateur);
          toast('Préférence enregistrée');
        },
        onError: (erreur) => toast(messageErreur(erreur, 'Impossible d’enregistrer la préférence')),
      },
    );
  };

  const soumettreMotDePasse = (): void => {
    if (!motDePasseActuel || !nouveauMotDePasse) {
      toast('Renseignez le mot de passe actuel et le nouveau');
      return;
    }
    changerMotDePasse.mutate(
      { motDePasseActuel, nouveauMotDePasse },
      {
        onSuccess: () => {
          setMotDePasseActuel('');
          setNouveauMotDePasse('');
          toast('Mot de passe modifié');
        },
        onError: (erreur) => toast(messageErreur(erreur, 'Impossible de modifier le mot de passe')),
      },
    );
  };

  const soumettreEmail = (): void => {
    if (!nouvelEmail.trim() || !motDePasseEmail) {
      toast('Renseignez la nouvelle adresse et votre mot de passe actuel');
      return;
    }
    changerEmail.mutate(
      { nouvelEmail: nouvelEmail.trim(), motDePasseActuel: motDePasseEmail },
      {
        onSuccess: (reponse) => {
          installerSession(reponse.utilisateur);
          setNouvelEmail('');
          setMotDePasseEmail('');
          setModifierEmailOuvert(false);
          toast('Adresse e-mail modifiée');
        },
        onError: (erreur) => toast(messageErreur(erreur, "Impossible de modifier l'adresse e-mail")),
      },
    );
  };

  const telechargerDonnees = (): void => {
    exporterDonnees.mutate(undefined, {
      onSuccess: (donnees) => {
        const blob = new Blob([JSON.stringify(donnees, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const lien = document.createElement('a');
        lien.href = url;
        lien.download = `mes-donnees-${utilisateur.id}.json`;
        lien.click();
        URL.revokeObjectURL(url);
        toast('Export de vos données téléchargé');
      },
      onError: (erreur) => toast(messageErreur(erreur, "Impossible d'exporter vos données")),
    });
  };

  const demanderLaSuppression = (): void => {
    demanderSuppression.mutate(undefined, {
      onSuccess: () => toast('Demande transmise au délégué à la protection des données'),
      onError: (erreur) => toast(messageErreur(erreur, 'Impossible de transmettre la demande')),
    });
  };

  return (
    <div className="mx-auto max-w-[1500px] space-y-6 p-4 sm:p-6">
      <div className="flex flex-col items-start justify-between gap-4 lg:flex-row lg:items-center">
        <div>
          <h1 className="font-display text-2xl font-bold text-codialis-navy">Mes paramètres</h1>
          <p className="mt-0.5 text-sm text-codialis-gray">
            Vos informations, vos préférences et votre sécurité
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={enregistrerProfil}
            disabled={modifierProfil.isPending}
            className="rounded-xl bg-codialis-navy px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-codialis-deep disabled:opacity-60"
          >
            {modifierProfil.isPending ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="min-w-0 space-y-6 lg:col-span-2">
          {/* Mon profil */}
          <div className="overflow-hidden rounded-2xl bg-white shadow-card">
            <div className="flex items-center justify-between gap-3 border-b border-gray-100 px-4 sm:px-6 py-4">
              <h2 className="font-display font-bold text-codialis-navy">Mon profil</h2>
            </div>
            <div className="grid grid-cols-1 gap-3 p-4 sm:p-6 sm:grid-cols-2">
              <div>
                <label htmlFor="settings-prenom" className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-codialis-navy">
                  Prénom
                </label>
                <input id="settings-prenom"
                  type="text"
                  autoComplete="given-name"
                  value={prenom}
                  onChange={(e) => setPrenom(e.target.value)}
                  className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-codialis-mint"
                />
              </div>
              <div>
                <label htmlFor="settings-nom" className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-codialis-navy">
                  Nom
                </label>
                <input id="settings-nom"
                  type="text"
                  autoComplete="family-name"
                  value={nom}
                  onChange={(e) => setNom(e.target.value)}
                  className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-codialis-mint"
                />
              </div>
              <div>
                <label htmlFor="settings-telephone" className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-codialis-navy">
                  Téléphone
                </label>
                <input id="settings-telephone"
                  type="tel"
                  autoComplete="tel"
                  value={telephone}
                  onChange={(e) => setTelephone(e.target.value)}
                  className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-codialis-mint"
                />
              </div>
              <div className="sm:col-span-2 min-w-0">
                <label htmlFor="settings-adresse-e-mail" className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-codialis-navy">
                  Adresse e-mail
                </label>
                <input id="settings-adresse-e-mail"
                  type="email"
                  value={utilisateur.email}
                  disabled
                  title="Modifier l'adresse exige votre mot de passe actuel"
                  className="w-full cursor-not-allowed rounded-xl border border-gray-200 bg-gray-100 px-3 py-2.5 text-sm text-codialis-gray"
                />
                {!modifierEmailOuvert ? (
                  <button
                    type="button"
                    onClick={() => setModifierEmailOuvert(true)}
                    className="mt-1.5 text-xs font-semibold text-codialis-sky hover:underline"
                  >
                    Modifier mon adresse e-mail
                  </button>
                ) : (
                  <div className="mt-3 space-y-2 rounded-xl bg-gray-50 p-3">
                    <div>
                      <label htmlFor="settings-nouvelle-adresse-e-mail" className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-codialis-navy">
                        Nouvelle adresse e-mail
                      </label>
                      <input id="settings-nouvelle-adresse-e-mail"
                        type="email"
                        autoComplete="email"
                        value={nouvelEmail}
                        onChange={(e) => setNouvelEmail(e.target.value)}
                        className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-codialis-mint"
                      />
                    </div>
                    <div>
                      <label htmlFor="settings-mot-de-passe-actuel" className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-codialis-navy">
                        Mot de passe actuel
                      </label>
                      <input id="settings-mot-de-passe-actuel"
                        type="password"
                        autoComplete="current-password"
                        value={motDePasseEmail}
                        onChange={(e) => setMotDePasseEmail(e.target.value)}
                        className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-codialis-mint"
                      />
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={soumettreEmail}
                        disabled={changerEmail.isPending}
                        className="rounded-xl bg-codialis-navy px-4 py-2 text-xs font-semibold text-white transition hover:bg-codialis-deep disabled:opacity-60"
                      >
                        {changerEmail.isPending ? 'Modification…' : "Changer l'adresse"}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setModifierEmailOuvert(false);
                          setNouvelEmail('');
                          setMotDePasseEmail('');
                        }}
                        className="rounded-xl border border-gray-200 bg-white px-4 py-2 text-xs font-semibold text-codialis-navy hover:bg-gray-50"
                      >
                        Annuler
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Notifications */}
          <div className="overflow-hidden rounded-2xl bg-white shadow-card">
            <div className="flex items-center justify-between gap-3 border-b border-gray-100 px-4 sm:px-6 py-4">
              <h2 className="font-display font-bold text-codialis-navy">Notifications</h2>
            </div>
            <div className="space-y-2 p-4 sm:p-6">
              <div className="flex items-center gap-3 rounded-xl bg-gray-50 px-3 py-2.5">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-codialis-navy">Alertes par e-mail</div>
                  <div className="text-[11px] text-codialis-gray">
                    Convocations, documents à signer, échéances
                  </div>
                </div>
                <Bascule
                  libelle="Alertes par e-mail"
                  checked={utilisateur.alertesEmail}
                  disabled={modifierPreferences.isPending}
                  onChange={(valeur) => basculerPreference('alertesEmail', valeur)}
                />
              </div>

              <div className="flex items-center gap-3 rounded-xl bg-gray-50 px-3 py-2.5">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-codialis-navy">Alertes par SMS</div>
                  <div className="text-[11px] text-codialis-gray">
                    Rappels de rendez-vous et relances urgentes
                  </div>
                </div>
                <Bascule
                  libelle="Alertes par SMS"
                  checked={utilisateur.alertesSms}
                  disabled={modifierPreferences.isPending}
                  onChange={(valeur) => basculerPreference('alertesSms', valeur)}
                />
              </div>

              <div className="flex items-center gap-3 rounded-xl bg-gray-50 px-3 py-2.5">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-codialis-navy">
                    Récapitulatif hebdomadaire
                  </div>
                  <div className="text-[11px] text-codialis-gray">Chaque lundi matin</div>
                </div>
                <Bascule
                  libelle="Récapitulatif hebdomadaire"
                  checked={utilisateur.recapHebdo}
                  disabled={modifierPreferences.isPending}
                  onChange={(valeur) => basculerPreference('recapHebdo', valeur)}
                />
              </div>
            </div>
          </div>

          {/* Sécurité */}
          <div className="overflow-hidden rounded-2xl bg-white shadow-card">
            <div className="flex items-center justify-between gap-3 border-b border-gray-100 px-4 sm:px-6 py-4">
              <h2 className="font-display font-bold text-codialis-navy">Sécurité</h2>
            </div>
            <div className="grid grid-cols-1 gap-3 p-4 sm:p-6 sm:grid-cols-2">
              <div>
                <label htmlFor="settings-mot-de-passe-actuel-2" className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-codialis-navy">
                  Mot de passe actuel
                </label>
                <input id="settings-mot-de-passe-actuel-2"
                  type="password"
                  autoComplete="current-password"
                  value={motDePasseActuel}
                  onChange={(e) => setMotDePasseActuel(e.target.value)}
                  className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-codialis-mint"
                />
              </div>
              <div>
                <label htmlFor="settings-nouveau-mot-de-passe" className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-codialis-navy">
                  Nouveau mot de passe
                </label>
                <input id="settings-nouveau-mot-de-passe"
                  type="password"
                  autoComplete="new-password"
                  value={nouveauMotDePasse}
                  onChange={(e) => setNouveauMotDePasse(e.target.value)}
                  className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-codialis-mint"
                />
                <p className="mt-1 text-[11px] text-codialis-gray">12 caractères minimum</p>
              </div>
              <div className="sm:col-span-2 min-w-0">
                <button
                  type="button"
                  onClick={soumettreMotDePasse}
                  disabled={changerMotDePasse.isPending}
                  className="rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-codialis-navy transition hover:bg-gray-50 disabled:opacity-60"
                >
                  {changerMotDePasse.isPending ? 'Modification…' : 'Changer le mot de passe'}
                </button>
              </div>
              <div className="sm:col-span-2 min-w-0">
                <div className="flex items-center gap-3 rounded-xl bg-gray-50 px-3 py-2.5">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-codialis-navy">
                      Double authentification
                    </div>
                    <div className="text-[11px] text-codialis-gray">
                      Code envoyé par SMS à la connexion
                    </div>
                  </div>
                  <Bascule
                    libelle="Double authentification"
                    checked={utilisateur.mfaActif}
                    disabled={modifierPreferences.isPending}
                    onChange={(valeur) => basculerPreference('mfaActif', valeur)}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Colonne droite */}
        <div className="space-y-6">
          {/* Affichage */}
          <div className="overflow-hidden rounded-2xl bg-white shadow-card">
            <div className="flex items-center justify-between gap-3 border-b border-gray-100 px-4 sm:px-6 py-4">
              <h2 className="font-display font-bold text-codialis-navy">Affichage</h2>
            </div>
            <div className="space-y-3 p-4 sm:p-6">
              <div>
                <div className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-codialis-navy">
                  Langue
                </div>
                <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-codialis-navy">
                  Français
                </div>
                <p className="mt-1 text-[11px] text-codialis-gray">
                  Seule langue disponible pour le moment
                </p>
              </div>

              <div className="flex items-center gap-3 rounded-xl bg-gray-50 px-3 py-2.5">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-codialis-navy">
                    Réduire les animations
                  </div>
                  <div className="text-[11px] text-codialis-gray">Confort de lecture · ce poste</div>
                </div>
                <Bascule libelle="Réduire les animations" checked={reduireAnimations} onChange={setReduireAnimations} />
              </div>

              {/* Apprenants, formateurs et pôle pédagogique seulement (TF-491). */}
              {peutAcceder('accessibility') && (
                <button
                  type="button"
                  onClick={() => navigate('/accessibility')}
                  className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-codialis-navy transition hover:bg-gray-50"
                >
                  Réglages d'accessibilité
                </button>
              )}
            </div>
          </div>

          {/* Mes données */}
          <div className="overflow-hidden rounded-2xl bg-white shadow-card">
            <div className="flex items-center justify-between gap-3 border-b border-gray-100 px-4 sm:px-6 py-4">
              <h2 className="font-display font-bold text-codialis-navy">Mes données</h2>
            </div>
            <div className="space-y-3 p-4 sm:p-6">
              <p className="text-sm text-codialis-gray">
                Vous pouvez demander une copie de vos données personnelles ou leur suppression.
              </p>
              <button
                type="button"
                onClick={telechargerDonnees}
                disabled={exporterDonnees.isPending}
                className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-codialis-navy transition hover:bg-gray-50 disabled:opacity-60"
              >
                {exporterDonnees.isPending ? 'Préparation…' : 'Télécharger mes données'}
              </button>
              <button
                type="button"
                onClick={demanderLaSuppression}
                disabled={demanderSuppression.isPending}
                className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-codialis-navy transition hover:bg-gray-50 disabled:opacity-60"
              >
                Demander la suppression
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
